/**
 * The project's task registry, read as plain summaries (RFC 0319 3e).
 *
 * The registry is a map on the project doc, which every surface already holds,
 * so these reads open nothing. Here rather than in one client because the
 * window and the voice (RFC 0307) both read it.
 */

import type { ProjectDoc } from '../project/types'
import type { TaskSummary } from './types'

/** Every task in the registry, as plain summaries. */
export function registryTasks(projectDoc: unknown): TaskSummary[] {
  const tasks = (projectDoc as ProjectDoc | undefined)?.tasks
  if (!tasks || typeof tasks !== 'object') return []
  return Object.entries(tasks).map(([id, entry]) => ({
    id,
    title: String(entry.title ?? ''),
    // String(): a scalar (RFC 0357 S11) is an object no `===` matches.
    status: String(entry.status) as TaskSummary['status'],
    ...(entry.priority
      ? { priority: String(entry.priority) as TaskSummary['priority'] }
      : {}),
    ...(entry.parentId ? { parentId: String(entry.parentId) } : {}),
    blockedBy: Array.isArray(entry.blockedBy)
      ? entry.blockedBy.map(String)
      : [],
    refs: Array.isArray(entry.refs)
      ? entry.refs.map((r) => ({
          type: String(r.type),
          target: String(r.target),
          ...(r.role ? { role: String(r.role) } : {}),
        }))
      : [],
    updatedAt: String(entry.updatedAt ?? ''),
  }))
}

/** A parent and the session's tasks under it. `parent` is absent for tasks
 *  with no parent in the registry. */
export interface TaskGroup {
  parent?: TaskSummary
  tasks: TaskSummary[]
}

/**
 * The session lens (RFC 0319 3d): the tasks whose refs name `sessionId`,
 * grouped under their parent. A parent is shown as the group's heading even
 * when it does not name the session itself, because that is where the work
 * belongs; a named task that is itself a parent of named tasks is a heading,
 * not a row.
 */
export function sessionTaskGroups(
  projectDoc: unknown,
  sessionId: string,
): TaskGroup[] {
  const all = registryTasks(projectDoc)
  const byId = new Map(all.map((t) => [t.id, t]))
  const named = all.filter((t) => t.refs.some((r) => r.target === sessionId))
  const groups = new Map<string, TaskGroup>()
  const loose: TaskSummary[] = []
  for (const t of named) {
    const parent = t.parentId ? byId.get(t.parentId) : undefined
    if (!parent) {
      loose.push(t)
      continue
    }
    const group = groups.get(parent.id) ?? { parent, tasks: [] }
    group.tasks.push(t)
    groups.set(parent.id, group)
  }
  const rows = loose.filter((t) => !groups.has(t.id))
  return [...groups.values(), ...(rows.length > 0 ? [{ tasks: rows }] : [])]
}
